<script lang="ts">
import ActivityIndicator from '@/components/v2/ActivityIndicator.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { AlertTriangle, Check, ChevronDown, Pencil, Trash2, X, Download, RotateCcw } from 'lucide-vue-next';
import CreateUpdateUserModal from '@/views/admin/PermissionAdmin/CreateUpdateUserModal.vue';

export default {
  name: 'PermissionAdmin',
  components: {
    ActivityIndicator,
    Toggle,
    Pencil,
    Trash2,
    Check,
    ChevronDown,
    X,
    AlertTriangle,
    Download,
    RotateCcw,
    CreateUpdateUserModal,
  },
};
</script>

<script setup lang="ts">
import { permissionService } from '@/services/api';
import { ref, onMounted, computed, watch } from 'vue';
import {
  AllowedPermissionsByRole,
  AWSUserStatus,
  UserPermissionDetails,
  UserPermissionDetailsWithRoles,
} from '@/types/UserPermissions';
import { UserRoles } from '@/types/UserPermissions';
import AppModal from '@/components/v2/AppModal.vue';
import { UserModalType, UserType } from '@/types/User';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import userService from '@/services/UserService';

const SECURITY_ROLES = ['admin', 'broker', 'client', 'market'];

// DATA
// ------------------------------------------------
const userData = ref<UserPermissionDetails[]>([]);
const roles = ref<UserRoles[]>([]);
const allowedPermissionsByRole = ref<AllowedPermissionsByRole[]>([]);
const loading = ref(false);
const updatingUser = ref(false);
const updatingPassword = ref(false);
const showCreateUserModal = ref(false);
const showUpdateUserModal = ref(false);
const showPasswordResetModal = ref(false);
const showDeleteUserModal = ref(false);

const searchQuery = ref<string>('');
const userTypeFilter = ref<HTMLElement>();
const selectedUserType = ref<string | undefined>();
const showDeletedUsers = ref<boolean>(false);
const showRestoreUserModal = ref(false);

//// role change modal
const userSelectedRole = ref<string>();
const selectedUser = ref<UserPermissionDetailsWithRoles | undefined>();
const userPermissions = ref<string[]>([]);
const userSelectedPermissions = ref<string[]>([]);

// COMPUTED
// ------------------------------------------------
const users = computed<UserPermissionDetailsWithRoles[]>(() => {
  return userData.value
    .map((user: UserPermissionDetails) => {
      const uniqueAccounts = uniqueAccountsForUser(user) ?? [];
      const userData = {
        ...user,
        //sort accounts parent first then alpha
        accounts: uniqueAccounts?.sort((a, b) => {
          if (a.parent && !b.parent) return -1;
          if (!a.parent && b.parent) return 1;
          if (a.accountName < b.accountName) return -1;
          if (a.accountName > b.accountName) return 1;
          return 0;
        }),
        roles:
          roles.value
            ?.find((role) => role.userSubId === user.userSubId)
            ?.roles?.filter((role) => SECURITY_ROLES.includes(role)) ?? [],
        isAdmin: false,
      };

      userData.isAdmin = userData.roles.includes('admin');
      return userData;
    })
    .filter((user) => {
      // this needs to be a fuzzy search across email, name, roles, and accounts
      if (searchQuery.value) {
        return (
          user.userEmail.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          user.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
          user.roles.join().toLowerCase().includes(searchQuery.value.toLowerCase())
        );
      }
      return true;
    })
    .filter((user) => {
      if (selectedUserType.value) {
        return user.userTypeName === selectedUserType.value;
      }
      return true;
    })
    .filter((user) => {
      // Filter out deleted users unless showDeletedUsers is true
      if (!showDeletedUsers.value && user.deletedAt) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      // sort on usertypeid, then user email
      if (a.userTypeId < b.userTypeId) return 1;
      if (a.userTypeId > b.userTypeId) return -1;
      if (a.userEmail < b.userEmail) return -1;
      if (a.userEmail > b.userEmail) return 1;
      return 0;
    });
});

const userTypes = computed(() => {
  return (
    userData.value?.reduce((acc, user) => {
      if (user.userTypeName && !acc.includes(user.userTypeName)) {
        acc.push(user.userTypeName);
      }
      return acc;
    }, [] as string[]) ?? []
  );
});

/**
 * Picks the first role from the array of SECURITY_ROLES.
 * The user could have multiple cognito roles, but we are moving to a single role per user
 * with the new permissions model.
 */
const userCurrentRole = computed<string>(() => {
  if (selectedUser.value) {
    for (const role of SECURITY_ROLES) {
      if (selectedUser.value?.roles.includes(role)) {
        return role;
      }
    }
  }

  return '';
});

// WATCHERS
// ---------
watch(userSelectedRole, () => {
  if (!userSelectedRole.value) {
    return;
  }

  // reset the selected permissions when the role changes
  if (userCurrentRole.value !== userSelectedRole.value) {
    userSelectedPermissions.value = [];
  } else {
    userSelectedPermissions.value = userPermissions.value;
  }
});

// METHODS
// ------------------------------------------------
async function refresh() {
  loading.value = true;
  [userData.value, roles.value, allowedPermissionsByRole.value] = await Promise.all([
    await permissionService.GetUserPermissionsV2(showDeletedUsers.value),
    await permissionService.GetAllUserRolesV2(),
    await permissionService.getAllowedPermissionsByRole(),
  ]);
  loading.value = false;
}

function uniqueAccountsForUser(user: UserPermissionDetails) {
  const uniqueAccountMap = new Map();
  user.accounts?.forEach((account) => {
    if (!uniqueAccountMap.has(account.accountId)) {
      uniqueAccountMap.set(account.accountId, account);
    }
  });
  return Array.from(uniqueAccountMap.values());
}

async function exportUserPermissions() {
  const seriesNames = ['User Type', 'Name', 'Email', 'Last Login Date', 'Roles', 'Permissions', 'Accounts'];
  const header = seriesNames.join(',');

  const csvRows = [header];
  userData.value.forEach((user) => {
    const userRoles = roles.value.find((u) => u.userSubId === user.userSubId);
    const joinedRoles = `"${userRoles?.roles?.join(', ')}"`;
    const permissions = `"${user.permissions?.join(', ')}"`;
    const accounts = `"${user.accounts?.map((a) => a.accountName).join(', ') ?? ''}"`;
    const row = [user.userTypeName, user.name, user.userEmail, user.activeDate, joinedRoles, permissions, accounts];
    csvRows.push(row.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'User_Permissions.csv';
  a.click();
}

function formatRoles(roles?: string[]): string | undefined {
  if (!roles) return undefined;
  return roles
    .filter((role) => role !== 'admin')
    .map((role) =>
      role
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(', ');
}

function formatPermissions(permissions?: string[]): string[][] {
  if (!permissions) return [];
  const permissionGroups = permissions
    .map((p) => p.split(':'))
    .reduce(
      (acc, [name, action]) => {
        if (!acc[action]) {
          acc[action] = [];
        }
        acc[action].push(name);
        return acc;
      },
      {} as Record<string, string[]>,
    );

  return Object.entries(permissionGroups).map(([action, permissions]) => [action, permissions.sort((a, b) => (a.localeCompare(b))).join(', ')]);
}

function editUser(user: UserPermissionDetailsWithRoles) {
  handleOpenUpdateUserModal(user);
}

function handleOpenDeleteModal(user: UserPermissionDetailsWithRoles) {
  selectedUser.value = user;
  showDeleteUserModal.value = true;
}

function handleOpenUpdateUserModal(user: UserPermissionDetailsWithRoles) {
  const userPerms = user.permissions ?? [];
  if (userPerms) {
    userPermissions.value = userPerms;
    userSelectedPermissions.value = userPerms;
  }

  selectedUser.value = user;
  showUpdateUserModal.value = true;
  userSelectedRole.value = userCurrentRole.value;
}

function setUserTypeFilter(userType?: string) {
  selectedUserType.value = userType;
  userTypeFilter.value?.blur();
}

function setSearchQuery(event: Event) {
  searchQuery.value = (event.target as HTMLInputElement).value;
}

function handleUserCreated(hasError: boolean) {
  if (!hasError) {
    handleCloseUserModal();
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Created' });
    refresh();
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to Create User', color: 'error' });
  }
}

function handleCloseUserModal() {
  showUpdateUserModal.value = false;
  showCreateUserModal.value = false;
  selectedUser.value = undefined;
  userSelectedRole.value = undefined;
}

function handleCloseDeleteModal() {
  showDeleteUserModal.value = false;
  selectedUser.value = undefined;
  userSelectedRole.value = undefined;
}

function handleUserUpdated(hasError: boolean) {
  handleCloseUserModal();

  if (!hasError) {
    refresh();
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to Update User', color: 'error' });
  }
}

//// password reset modal
async function handleResetUserPassword(user?: UserPermissionDetailsWithRoles) {
  if (!user?.userId) return;

  try {
    updatingPassword.value = true;
    await userService.resetUserPassword(user.userId);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Password Reset' });
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to reset password', color: 'error' });
  } finally {
    showPasswordResetModal.value = false;
    showUpdateUserModal.value = false;
    updatingPassword.value = false;
    await refresh();
  }
}

//// delete user modal
async function deleteUser(user: UserPermissionDetails) {
  if (user.userTypeId === UserType.Manual) {
    try {
      updatingUser.value = true;
      await permissionService.DeleteUser(user.userSubId);
      Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Deleted' });
    } catch (e) {
      Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to delete user', color: 'error' });
    } finally {
      handleCloseDeleteModal();
      updatingUser.value = false;
      await refresh();
    }
  }
}

function handleOpenRestoreModal(user: UserPermissionDetailsWithRoles) {
  selectedUser.value = user;
  showRestoreUserModal.value = true;
}

function handleCloseRestoreModal() {
  showRestoreUserModal.value = false;
  selectedUser.value = undefined;
}

async function restoreUser(user: UserPermissionDetails) {
  try {
    updatingUser.value = true;
    await permissionService.RestoreUser(user.userSubId);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'User Restored' });
  } catch (e) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to restore user', color: 'error' });
  } finally {
    handleCloseRestoreModal();
    updatingUser.value = false;
    await refresh();
  }
}

// LIFECYCLE HOOKS
// ==================================================
onMounted(async () => {
  await refresh();
});
</script>

<template>
  <div class="inner tw:flex tw:h-full tw:flex-col tw:items-center tw:p-4">
    <div v-if="loading" class="tw:mt-[8%] tw:flex tw:w-full tw:justify-around">
      <span class="tw:loading tw:loading-lg tw:loading-spinner"></span>
    </div>

    <div v-else class="tw:flex tw:h-full tw:w-full tw:flex-col tw:md:w-full tw:lg:w-[90%] tw:2xl:w-[80%]">
      <div class="tw:flex tw:justify-between">
        <h2 class="tw:mb-2 tw:ml-2">Manage User Permissions</h2>
      </div>

      <!-- FILTERS -->
      <div class="tw:mb-2 tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-4 tw:px-1">
        <div class="tw:relative tw:flex tw:w-1/3 tw:flex-row">
          <input
            type="text"
            placeholder="Search"
            @input="setSearchQuery"
            :value="searchQuery"
            class="tw:input tw:h-10 tw:w-full tw:rounded-md tw:border-solid" />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="tw:absolute tw:top-2 tw:right-1 tw:text-slate-400 tw:hover:text-slate-700">
            <X />
          </button>
        </div>

        <div class="tw:dropdown tw:dropdown-bottom tw:ml-0">
          <label
            tabindex="0"
            class="tw:btn tw:m-1 tw:h-10 tw:min-h-5 tw:rounded-md tw:bg-gray-200/50 tw:text-xs tw:hover:bg-gray-200">
            {{ selectedUserType ? selectedUserType : 'All Users' }}
            <ChevronDown :size="18" />
          </label>
          <ul
            ref="userTypeFilter"
            tabindex="0"
            class="tw:dropdown-content tw:menu tw:z-100 tw:w-52 tw:rounded-box tw:bg-base-100 tw:p-2 tw:shadow-sm">
            <li>
              <a @click="setUserTypeFilter(undefined)" class="tw:w-full tw:text-gray-700">
                All Users
                <Check :size="15" v-if="!selectedUserType" />
              </a>
            </li>
            <li v-for="userType in userTypes" :key="userType">
              <a @click="setUserTypeFilter(userType)" class="tw:w-full tw:text-gray-700">
                {{ userType }}
                <Check :size="15" v-if="selectedUserType && selectedUserType === userType" />
              </a>
            </li>
          </ul>
        </div>

        <div class="tw:ml-0 tw:mr-auto">
          <Toggle
            id="show-deleted-users"
            label="Show Deleted Users"
            v-model="showDeletedUsers"
            @update:modelValue="refresh"
            size="sm" />
        </div>

        <button
          @click="showCreateUserModal = true"
          class="tw:btn tw:h-10 tw:min-h-0 tw:w-28 tw:justify-self-end tw:rounded-md tw:text-white tw:btn-neutral">
          Create User
        </button>

        <div data-tip="Export All as .CSV" class="tw:tooltip tw:tooltip-top tw:z-50 tw:mr-2 tw:flex">
          <div @click="exportUserPermissions" class="tw:btn tw:btn-sm tw:btn-primary">
            <Download class="tw:justify-self-center" :size="15" color="white" />
          </div>
        </div>
      </div>

      <!-- USER TABLE -->
      <table
        class="tw:table tw:mb-[33vh] tw:w-full tw:table-auto tw:border tw:border-solid tw:border-slate-200 tw:table-xs tw:pt-0.5 tw:text-xs tw:font-medium tw:text-slate-800">
        <thead class="tw:sticky tw:top-[64px] tw:z-50 tw:shadow-xs">
          <tr class="tw:h-8 tw:bg-white">
            <th class="tw:mx-0 tw:px-0"></th>
            <th class="tw:px-0 tw:text-center">User Type</th>
            <th>Email</th>
            <th>Name</th>
            <th>Roles</th>
            <th>Permissions</th>
            <th class="">Accounts</th>
            <th class="tw:pl-3.5">Activity</th>
            <th class="tw:text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.userSubId"
            class="tw:group tw:h-12"
            :class="{ 'tw:opacity-50': user.deletedAt }">
            <td class="tw:mx-0 tw:w-4 tw:pr-0 tw:pl-3">
              <div
                v-if="user.userStatus === AWSUserStatus.FORCE_CHANGE_PASSWORD"
                class="tw:tooltip tw:tooltip-bottom tw:flex"
                data-tip="Password Reset Requested">
                <AlertTriangle :size="18" class="tw:stroke-red-400" />
              </div>
            </td>
            <td class="tw:px-0 tw:text-center">{{ user.userTypeName }}</td>
            <td>{{ user.userEmail }}</td>
            <td>{{ user.name || '-' }}</td>

            <!-- ROLES -->
            <td class="tw:font-normal">
              <span v-if="user.isAdmin" class="tw:font-bold tw:text-amber-700">
                Admin<span v-if="user.roles.length > 1" class="tw:text-black">, </span>
              </span>
              <span>{{ formatRoles(user.roles) || (user.isAdmin ? '' : '-') }}</span>
            </td>

            <!-- PERMISSIONS -->
            <td>
              <div class="tw:flex tw:flex-col tw:gap-1" v-if="formatPermissions(user.permissions).length > 0">
                <span v-for="permsByType in formatPermissions(user.permissions)" :key="permsByType[0]">
                  <span v-for="(perm, index) in permsByType" :key="perm">
                    <span v-if="index === 0" class="tw:font-normal">{{ perm }}: </span>
                    <span v-else class="tw:font-bold">{{ perm }}</span>
                  </span>
                </span>
              </div>
              <span v-else>-</span>
            </td>

            <!-- Accounts -->
            <td>
              <template v-if="user.accounts && user.accounts.length > 0">
                <div
                  v-if="user.accounts && user.accounts.length > 1"
                  class="tw:dropdown-hover tw:dropdown tw:dropdown-left tw:cursor-pointer">
                  <div>
                    <a class="tw:font-bold tw:text-secondary">
                      {{ user.accounts.length + ' ' + (user.accounts.length > 1 ? 'Accounts' : 'Account') }}
                    </a>
                  </div>
                  <ul
                    class="tw:dropdown-content tw:menu tw:z-1 tw:max-h-96 tw:w-80 tw:flex-nowrap tw:gap-1 tw:overflow-y-auto tw:rounded-lg tw:bg-base-100 tw:p-2 tw:text-xs tw:shadow-sm">
                    <li
                      v-for="account in user.accounts"
                      :key="account.accountId"
                      class="tw:flex tw:flex-row tw:rounded tw:border tw:border-solid tw:border-base-200">
                      <span class="tw:flex tw:grow tw:justify-between tw:pl-2">
                        <span>{{ account.accountName }}</span>
                        <span
                          v-if="account.parent"
                          class="tw:ml-2 tw:inline-flex tw:h-5 tw:w-fit tw:self-center tw:rounded-xl tw:bg-blue-200 tw:px-2 tw:align-baseline tw:text-[8pt] tw:text-blue-900">
                          <div class="tw:self-center">Parent</div>
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>

                <div
                  v-if="user.accounts && user.accounts.length === 1"
                  class="tw:dropdown-hover tw:dropdown tw:dropdown-left tw:cursor-pointer">
                  <div
                    class="tw:max-w-36 tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap"
                    :title="`${user.accounts[0].accountName}`">
                    {{ user.accounts[0].accountName }}
                  </div>
                </div>
              </template>
              <div v-else>-</div>
            </td>

            <!-- Activity -->
            <td>
              <ActivityIndicator v-if="user.activeDate" :last-active-date="user.activeDate"></ActivityIndicator>
              <span v-else class="tw:ml-2">-</span>
            </td>

            <!-- Actions-->
            <td class="tw:text-center">
              <div class="tw:flex tw:justify-center tw:gap-1 tw:*:flex tw:*:items-center tw:*:rounded-sm">
                <button
                  @click="editUser(user)"
                  class="tw:p-2 tw:opacity-20 tw:transition-all tw:group-hover:opacity-100 tw:hover:bg-slate-200 tw:hover:text-cyan-400">
                  <Pencil :size="16" />
                </button>
                <button
                  v-if="user.userTypeId === UserType.Manual && !user.deletedAt"
                  @click="handleOpenDeleteModal(user)"
                  class="tw:p-2 tw:opacity-20 tw:transition-all tw:group-hover:opacity-100 tw:hover:bg-slate-200 tw:hover:text-red-400"
                  title="Delete User">
                  <Trash2 :size="16"></Trash2>
                </button>
                <button
                  v-if="user.userTypeId === UserType.Manual && user.deletedAt"
                  @click="handleOpenRestoreModal(user)"
                  class="tw:p-2 tw:opacity-20 tw:transition-all tw:group-hover:opacity-100 tw:hover:bg-slate-200 tw:hover:text-green-400"
                  title="Restore User">
                  <RotateCcw :size="16"></RotateCcw>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CreateUpdateUserModal
      v-if="(showUpdateUserModal && selectedUser) || showCreateUserModal"
      :show="showUpdateUserModal || showCreateUserModal"
      :user="selectedUser ?? undefined"
      :permissions="allowedPermissionsByRole"
      :type="showCreateUserModal ? UserModalType.Create : UserModalType.Update"
      :sso="(selectedUser && selectedUser.userTypeId === UserType.SSO) ?? false"
      @cancel="handleCloseUserModal"
      @updated="handleUserUpdated"
      @reset-password="showPasswordResetModal = true"
      @created="handleUserCreated" />

    <!-- PASSWORD RESET MODAL -->
    <AppModal
      v-if="showPasswordResetModal && selectedUser"
      :show="showPasswordResetModal"
      action-button-label="Reset"
      :handle-cancel="() => (showPasswordResetModal = false)"
      :handle-action="() => handleResetUserPassword(selectedUser)"
      :loading="updatingPassword">
      <template v-slot:title>
        <h2 class="tw:font-normal tw:text-red-500">Reset Password</h2>
      </template>
      <template v-slot:content>
        <p class="tw:text-md">
          Are you sure you want to reset the password for
          <span class="tw:text-red-500">{{ selectedUser.userEmail }}</span
          >?
        </p>
      </template>
    </AppModal>

    <!-- DELETE USER MODAL -->
    <AppModal
      v-if="showDeleteUserModal && selectedUser"
      :show="showDeleteUserModal"
      action-button-label="Delete"
      :handle-cancel="handleCloseDeleteModal"
      :handle-action="() => selectedUser && deleteUser(selectedUser)"
      :loading="updatingUser">
      <template v-slot:title>
        <h2 class="tw:font-normal tw:text-red-500">Delete User</h2>
      </template>
      <template v-slot:content>
        <p class="tw:text-md tw:text-center">
          Are you sure you want to delete user
          <span class="tw:text-red-500">{{ selectedUser.userEmail }}</span
          >? This will disable their account access.
        </p>
      </template>
    </AppModal>

    <!-- RESTORE USER MODAL -->
    <AppModal
      v-if="showRestoreUserModal && selectedUser"
      :show="showRestoreUserModal"
      action-button-label="Restore"
      :handle-cancel="handleCloseRestoreModal"
      :handle-action="() => selectedUser && restoreUser(selectedUser)"
      :loading="updatingUser">
      <template v-slot:title>
        <h2 class="tw:font-normal tw:text-green-500">Restore User</h2>
      </template>
      <template v-slot:content>
        <p class="tw:text-md tw:text-center">
          Are you sure you want to restore user
          <span class="tw:text-green-500">{{ selectedUser.userEmail }}</span
          >? This will re-enable their account access.
        </p>
      </template>
    </AppModal>
  </div>
</template>

<style scoped></style>
